import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { motion, AnimatePresence } from "framer-motion";
import {
  fetchJobsForScope,
  selectScopeJobs,
  selectScopeJobsLoaded,
} from "../../../redux/slices/job-scope-slice";
import {
  fetchRoundsForJob,
  selectRoundsForJob,
} from "../../../redux/slices/question-bank-slice";
import {
  cloneTemplateToJob,
  selectIsCloning,
  selectCloneError,
} from "../../../redux/slices/question-template-slice";

export default function CloneToJobModal({ open, template, onClose, onCloned }) {
  const dispatch = useDispatch();
  const jobs = useSelector(selectScopeJobs);
  const jobsLoaded = useSelector(selectScopeJobsLoaded);
  const cloning = useSelector(selectIsCloning);
  const cloneError = useSelector(selectCloneError);

  const [jobId, setJobId] = useState(null);
  const [round, setRound] = useState("");
  const rounds = useSelector(selectRoundsForJob(jobId));

  useEffect(() => {
    if (open && !jobsLoaded) dispatch(fetchJobsForScope());
  }, [dispatch, open, jobsLoaded]);

  useEffect(() => {
    if (open) {
      setJobId(null);
      setRound("");
    }
  }, [open]);

  useEffect(() => {
    if (jobId != null) dispatch(fetchRoundsForJob({ jobId }));
  }, [dispatch, jobId]);

  const canClone = template?.templateId != null && jobId != null && round;

  const handleClone = async () => {
    if (!canClone || cloning) return;
    const result = await dispatch(
      cloneTemplateToJob({
        templateId: template.templateId,
        jobId,
        interviewRound: round,
      }),
    );
    if (cloneTemplateToJob.fulfilled.match(result)) {
      onCloned?.(result.payload);
      onClose?.();
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            style={backdrop}
          />
          <motion.div
            initial={{ opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.96 }}
            style={dialog}
          >
            <div style={hdr}>
              <div style={hdrTitle}>Clone template to job</div>
              <div style={hdrSub}>
                {template?.name || template?.questionText?.slice(0, 60) || ""}
              </div>
            </div>
            <div style={body}>
              <div style={label}>Target job</div>
              <select
                value={jobId ?? ""}
                onChange={(e) => {
                  const v = e.target.value;
                  setJobId(v ? Number(v) : null);
                  setRound("");
                }}
                style={select}
              >
                <option value="">— pick a job —</option>
                {jobs.map((j) => (
                  <option key={j.id} value={j.id}>
                    {j.name}
                  </option>
                ))}
              </select>

              <div style={{ ...label, marginTop: 12 }}>Interview round</div>
              <select
                value={round}
                onChange={(e) => setRound(e.target.value)}
                disabled={!jobId || rounds.length === 0}
                style={{
                  ...select,
                  opacity: !jobId || rounds.length === 0 ? 0.5 : 1,
                }}
              >
                <option value="">
                  {!jobId
                    ? "Pick a job first"
                    : rounds.length === 0
                      ? "No rounds defined for this job"
                      : "— pick a round —"}
                </option>
                {rounds.map((r) => (
                  <option key={r} value={r}>
                    {r}
                  </option>
                ))}
              </select>

              {cloneError && <div style={errorBanner}>{cloneError}</div>}
            </div>
            <div style={footer}>
              <button onClick={onClose} style={ghostBtn} disabled={cloning}>
                Cancel
              </button>
              <button
                onClick={handleClone}
                disabled={!canClone || cloning}
                style={{
                  ...primaryBtn,
                  opacity: !canClone || cloning ? 0.5 : 1,
                  cursor: !canClone || cloning ? "not-allowed" : "pointer",
                }}
              >
                {cloning ? "Cloning…" : "Clone"}
              </button>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}

const backdrop = {
  position: "fixed",
  inset: 0,
  background: "rgba(15, 23, 42, 0.4)",
  zIndex: 90,
};
const dialog = {
  position: "fixed",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: 420,
  background: "white",
  borderRadius: 10,
  boxShadow: "0 20px 60px rgba(15, 23, 42, 0.2)",
  zIndex: 100,
  display: "flex",
  flexDirection: "column",
  overflow: "hidden",
};
const hdr = {
  padding: "14px 18px",
  borderBottom: "1px solid #E2E8F0",
};
const hdrTitle = { fontSize: 15, fontWeight: 700, color: "#0F172A" };
const hdrSub = { fontSize: 11, color: "#64748B", marginTop: 2 };
const body = { padding: "16px 18px" };
const footer = {
  padding: "12px 18px",
  borderTop: "1px solid #E2E8F0",
  display: "flex",
  justifyContent: "flex-end",
  gap: 8,
  background: "#F8FAFC",
};
const label = {
  fontSize: 10,
  color: "#64748B",
  marginBottom: 4,
  fontWeight: 600,
  textTransform: "uppercase",
  letterSpacing: 0.4,
};
const select = {
  width: "100%",
  padding: "7px 10px",
  border: "1px solid #E2E8F0",
  borderRadius: 6,
  fontSize: 13,
  color: "#0F172A",
  outline: "none",
  background: "white",
  boxSizing: "border-box",
};
const primaryBtn = {
  padding: "8px 16px",
  borderRadius: 6,
  background: "#7B5AC4",
  color: "white",
  border: "none",
  fontWeight: 600,
  fontSize: 12,
  cursor: "pointer",
};
const ghostBtn = {
  padding: "8px 14px",
  borderRadius: 6,
  background: "white",
  color: "#475569",
  border: "1px solid #E2E8F0",
  fontWeight: 600,
  fontSize: 12,
  cursor: "pointer",
};
const errorBanner = {
  background: "#FEE2E2",
  color: "#991B1B",
  padding: "8px 10px",
  borderRadius: 6,
  fontSize: 12,
  marginTop: 12,
};
